/*
  ANINA · Reels (sections/anina-reels.liquid)
  Faixa: cada cartão toca a prévia sem som só enquanto está visível (e carrega o vídeo só nessa hora).
  Clique: abre o player em tela cheia com som, barras de progresso, legenda, perfume e link do Instagram.
  Navegação no player: setas, teclado (← →, Esc) e deslizar o dedo.
*/

class AninaReels extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-reel]')];
    this.trilho = this.querySelector('[data-trilho]');
    this.player = this.querySelector('[data-player]');
    if (!this.cards.length || !this.player) return;

    this.tela = this.player.querySelector('.anina-reels__tela');
    this.video = this.player.querySelector('[data-video]');
    this.fundo = this.player.querySelector('[data-fundo]');
    this.barras = this.player.querySelector('[data-barras]');
    this.botaoSom = this.player.querySelector('[data-som]');
    this.reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.comSom = true;
    this.atual = 0;

    this.barras.innerHTML = this.cards.map(() => '<span class="anina-reels__barra"><i></i></span>').join('');
    this.preencher = [...this.barras.querySelectorAll('i')];

    // Prévia em loop só para os cartões que estão na tela
    if (!this.reduzido && 'IntersectionObserver' in window) {
      this.observador = new IntersectionObserver((entradas) => {
        entradas.forEach((e) => (e.isIntersecting ? this.tocarCard(e.target) : this.pararCard(e.target)));
      }, { threshold: 0.6 });
      this.cards.forEach((c) => this.observador.observe(c));
    }

    this.aoClicarCard = (e) => {
      const card = e.target.closest('[data-reel]');
      if (!card || !card.dataset.full) return;
      e.preventDefault();
      this.abrir(this.cards.indexOf(card));
    };
    this.addEventListener('click', this.aoClicarCard);

    this.querySelector('[data-anterior]')?.addEventListener('click', () => this.rolar(-1));
    this.querySelector('[data-proximo]')?.addEventListener('click', () => this.rolar(1));

    this.player.querySelector('[data-fechar]').addEventListener('click', () => this.fechar());
    this.player.querySelector('[data-ant]').addEventListener('click', () => this.mostrar(this.atual - 1));
    this.player.querySelector('[data-prox]').addEventListener('click', () => this.mostrar(this.atual + 1));
    this.botaoSom.addEventListener('click', () => this.alternarSom());
    this.player.addEventListener('close', () => this.aoFechar());
    this.player.addEventListener('click', (e) => { if (e.target === this.player) this.fechar(); });
    this.video.addEventListener('click', () => this.alternarPausa());
    this.video.addEventListener('timeupdate', () => this.progresso());
    this.video.addEventListener('ended', () => {
      if (this.atual < this.cards.length - 1) this.mostrar(this.atual + 1);
      else { this.video.currentTime = 0; this.video.play().catch(() => {}); }
    });

    this.aoTeclar = (e) => {
      if (!this.player.open) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); this.mostrar(this.atual + 1); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); this.mostrar(this.atual - 1); }
    };
    document.addEventListener('keydown', this.aoTeclar);

    this.tela.addEventListener('touchstart', (e) => { this.t0 = e.touches[0]; }, { passive: true });
    this.tela.addEventListener('touchend', (e) => {
      if (!this.t0) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - this.t0.clientX;
      const dy = t.clientY - this.t0.clientY;
      this.t0 = null;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 50) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        if (dy > 90 && this.video.currentTime < 0.6) return this.fechar();
        this.mostrar(this.atual + (dy < 0 ? 1 : -1));
      } else {
        this.mostrar(this.atual + (dx < 0 ? 1 : -1));
      }
    });

    this.atualizarSetas = () => {
      const a = this.querySelector('[data-anterior]');
      const p = this.querySelector('[data-proximo]');
      if (!a || !p) return;
      a.disabled = this.trilho.scrollLeft < 8;
      p.disabled = this.trilho.scrollLeft + this.trilho.clientWidth > this.trilho.scrollWidth - 8;
    };
    this.trilho.addEventListener('scroll', this.atualizarSetas, { passive: true });
    this.atualizarSetas();
  }

  disconnectedCallback() {
    this.observador?.disconnect();
    document.removeEventListener('keydown', this.aoTeclar);
    this.removeEventListener('click', this.aoClicarCard);
  }

  tocarCard(card) {
    const v = card.querySelector('video');
    if (!v || !card.dataset.card || this.player.open) return;
    if (!v.src) {
      v.src = card.dataset.card;
      v.addEventListener('timeupdate', () => {
        if (v.duration) card.style.setProperty('--p', (v.currentTime / v.duration).toFixed(3));
      });
      v.addEventListener('playing', () => card.classList.add('is-tocando'));
    }
    v.play().catch(() => {});
  }

  pararCard(card) {
    const v = card.querySelector('video');
    if (v && v.src) v.pause();
  }

  rolar(dir) {
    const item = this.trilho.querySelector('.anina-reels__item');
    if (!item) return;
    const passo = (item.getBoundingClientRect().width + 16) * Math.max(1, Math.floor(this.trilho.clientWidth / item.offsetWidth) - 1);
    this.trilho.scrollBy({ left: dir * passo, behavior: this.reduzido ? 'auto' : 'smooth' });
  }

  abrir(i) {
    this.cards.forEach((c) => this.pararCard(c));
    this.player.showModal();
    document.documentElement.style.overflow = 'hidden';
    this.mostrar(i);
  }

  mostrar(i) {
    if (i < 0 || i >= this.cards.length) return;
    this.atual = i;
    const c = this.cards[i];
    const d = c.dataset;

    this.video.src = d.full;
    this.video.poster = d.poster || '';
    this.video.muted = !this.comSom;
    this.fundo.style.backgroundImage = d.poster ? `url("${d.poster}")` : '';
    this.tela.classList.remove('is-pausado');

    const legenda = c.querySelector('[data-legenda]');
    this.player.querySelector('[data-texto]').innerHTML = legenda ? legenda.innerHTML : '';

    const prod = this.player.querySelector('[data-produto]');
    if (d.produtoUrl) {
      prod.href = d.produtoUrl;
      prod.hidden = false;
      prod.querySelector('[data-produto-titulo]').textContent = d.produtoTitulo || '';
      prod.querySelector('[data-produto-preco]').textContent = d.produtoPreco || '';
      const img = prod.querySelector('[data-produto-img]');
      img.hidden = !d.produtoImg;
      if (d.produtoImg) img.src = d.produtoImg;
    } else {
      prod.hidden = true;
    }

    this.player.querySelector('[data-ig]').href = d.link;
    this.player.querySelector('[data-ant]').disabled = i === 0;
    this.player.querySelector('[data-prox]').disabled = i === this.cards.length - 1;
    this.preencher.forEach((b, k) => b.style.setProperty('--p', k < i ? 1 : 0));
    this.atualizarBotaoSom();

    this.video.play().catch(() => {
      // Navegador bloqueou o som: toca mudo e deixa o botão de som em destaque
      this.comSom = false;
      this.video.muted = true;
      this.atualizarBotaoSom();
      this.video.play().catch(() => {});
    });
  }

  progresso() {
    const b = this.preencher[this.atual];
    if (b && this.video.duration) b.style.setProperty('--p', (this.video.currentTime / this.video.duration).toFixed(3));
  }

  alternarSom() {
    this.comSom = !this.comSom;
    this.video.muted = !this.comSom;
    if (this.comSom && this.video.paused) this.video.play().catch(() => {});
    this.atualizarBotaoSom();
  }

  atualizarBotaoSom() {
    this.botaoSom.setAttribute('aria-pressed', String(this.comSom));
    this.botaoSom.setAttribute('aria-label', this.comSom ? 'Desativar som' : 'Ativar som');
  }

  alternarPausa() {
    if (this.video.paused) {
      this.video.play().catch(() => {});
      this.tela.classList.remove('is-pausado');
    } else {
      this.video.pause();
      this.tela.classList.add('is-pausado');
    }
  }

  fechar() {
    if (this.player.open) this.player.close();
  }

  aoFechar() {
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    document.documentElement.style.overflow = '';
    // Volta a prévia dos cartões que continuam na tela
    if (this.observador) {
      this.cards.forEach((c) => {
        const r = c.getBoundingClientRect();
        if (r.right > 0 && r.left < window.innerWidth && r.bottom > 0 && r.top < window.innerHeight) this.tocarCard(c);
      });
    }
    this.cards[this.atual]?.focus({ preventScroll: true });
  }
}

if (!customElements.get('anina-reels')) customElements.define('anina-reels', AninaReels);